/**
 * 工时记录 API 调用层
 */
import apiClient from '@/utils/apiClient';
import type {
  WorkHour,
  WorkHourCreateInput,
  WorkHourBatchInput,
  WorkHourUpdateInput,
} from '@/types/work-hour';
import type { CalendarWorkHour } from '@/types/hours-calendar';
import { getWorkdaysByDateRange } from './workdays';
import { createLogger } from '@/utils/logger';

const logger = createLogger('WorkHoursAPI');

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  total?: number;
  error?: string;
  message?: string;
}

/**
 * 根据分配模式分配工时到各个日期
 */
function allocateHours(
  workdays: string[],
  input: WorkHourCreateInput
): WorkHourBatchInput[] {
  const records: WorkHourBatchInput[] = [];
  const { taskId, userId, hoursType, mode, hours, description } = input;

  if (mode === 'single') {
    // 单日模式：使用 startDate（不依赖 workdays 参数）
    records.push({
      taskId,
      userId,
      workDate: input.startDate,
      [hoursType === 'planned' ? 'plannedHours' : 'actualHours']: hours,
      description,
    });
  } else if (mode === 'total') {
    // 总计模式：平均分配到工作日，保证总和严格相等
    // 1. 计算基础分配（保留 1 位小数）
    const baseHours = Math.floor((hours / workdays.length) * 10) / 10;

    // 2. 计算已分配总和
    const allocatedTotal = baseHours * workdays.length;

    // 3. 计算余数（保留 1 位小数）
    const remainder = Math.round((hours - allocatedTotal) * 10) / 10;

    // 4. 计算需要补偿的天数（每天补 0.1）
    const compensateDays = Math.round(remainder * 10);

    // 5. 分配工时
    workdays.forEach((date, index) => {
      // 前 compensateDays 天补偿 0.1 小时
      const finalHours = index < compensateDays
        ? Math.round((baseHours + 0.1) * 10) / 10
        : baseHours;

      records.push({
        taskId,
        userId,
        workDate: date,
        [hoursType === 'planned' ? 'plannedHours' : 'actualHours']: finalHours,
        description,
      });
    });
  } else if (mode === 'daily') {
    // 每天模式：每个工作日相同工时
    workdays.forEach((date) => {
      records.push({
        taskId,
        userId,
        workDate: date,
        [hoursType === 'planned' ? 'plannedHours' : 'actualHours']: hours,
        description,
      });
    });
  }

  return records;
}

/**
 * 批量创建/更新工时记录
 * 前端处理分配逻辑，后端只负责存储
 */
export async function batchCreateWorkHours(
  input: WorkHourCreateInput
): Promise<WorkHour[]> {
  // 根据模式决定是否需要工作日计算
  if (input.mode === 'single') {
    // ✅ 单日模式：直接分配，不检查工作日
    const records = allocateHours([], input); // 传入空数组，allocateHours 内部会使用 input.startDate

    const response = (await apiClient.post('/work-hours', {
      records,
    })) as ApiResponse<WorkHour[]>;

    if (!response.success) {
      throw new Error(response.error || '批量创建工时失败');
    }

    return response.data || [];
  } else {
    // ✅ 总计/每天模式：查询工作日并分配
    const workdayData = await getWorkdaysByDateRange(
      input.startDate,
      input.endDate
    );
    const workdays = workdayData.filter((d) => d.isWorkday).map((d) => d.date);

    if (workdays.length === 0) {
      throw new Error('选择的日期范围内没有工作日');
    }

    const records = allocateHours(workdays, input);

    const response = (await apiClient.post('/work-hours', {
      records,
    })) as ApiResponse<WorkHour[]>;

    if (!response.success) {
      throw new Error(response.error || '批量创建工时失败');
    }

    return response.data || [];
  }
}

/**
 * 查询工时记录列表
 */
export async function getWorkHours(params: {
  taskId?: string;
  userId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ data: WorkHour[]; total: number }> {
  const searchParams = new URLSearchParams();

  if (params.taskId) searchParams.append('task_id', params.taskId);
  if (params.userId) searchParams.append('user_id', params.userId);
  if (params.projectId) searchParams.append('project_id', params.projectId);
  if (params.startDate) searchParams.append('start_date', params.startDate);
  if (params.endDate) searchParams.append('end_date', params.endDate);

  const response = (await apiClient.get(
    `/work-hours?${searchParams}`
  )) as ApiResponse<WorkHour[]>;

  // 如果响应没有success字段或success为false，返回空数据而不是抛出错误
  if (!response || !response.success) {
    logger.warn('获取工时列表失败:', response?.error);
    return {
      data: [],
      total: 0,
    };
  }

  return {
    data: Array.isArray(response.data) ? response.data : [],
    total: response.total || 0,
  };
}

/**
 * 更新工时记录
 */
export async function updateWorkHour(
  id: string,
  input: WorkHourUpdateInput
): Promise<WorkHour> {
  const response = (await apiClient.put(
    `/work-hours/${id}`,
    input
  )) as ApiResponse<WorkHour>;

  if (!response.success) {
    throw new Error(response.error || '更新工时失败');
  }

  if (!response.data) {
    throw new Error('更新工时返回数据为空');
  }

  return response.data;
}

/**
 * 删除工时记录（选择性删除）
 * @param id 工时记录ID
 * @param hoursType 工时类型：'planned'=只删除计划工时，'actual'=只删除实际工时，不传=删除整条记录
 */
export async function deleteWorkHour(
  id: string,
  hoursType?: 'planned' | 'actual'
): Promise<void> {
  const params = hoursType ? `?hours_type=${hoursType}` : '';
  const url = `/work-hours/${id}${params}`;

  logger.debug('发起删除请求:', {
    id,
    hoursType,
    params,
    url,
  });

  const response = (await apiClient.delete(url)) as ApiResponse<null>;

  logger.debug('删除响应:', response);

  if (!response.success) {
    throw new Error(response.error || '删除工时失败');
  }
}

/**
 * 查询工时日历数据
 */
export async function getWorkHoursCalendar(params: {
  projectId?: string;
  requirementId?: string | null;
  projectIds?: string[];
  userIds: string[];
  startDate: string;
  endDate: string;
}): Promise<CalendarWorkHour[]> {
  logger.debug('getWorkHoursCalendar called:', params);

  const { projectId, requirementId, projectIds, userIds, startDate, endDate } = params;

  // 构建查询参数
  const queryParams = new URLSearchParams();

  // 项目模式参数
  if (projectId) {
    queryParams.append('project_id', projectId);
    if (requirementId) {
      queryParams.append('requirement_id', requirementId);
    }
  }
  // 全局模式参数
  else if (projectIds && projectIds.length > 0) {
    queryParams.append('project_ids', projectIds.join(','));
  }

  // 公共参数
  queryParams.append('user_ids', userIds.join(','));
  queryParams.append('start_date', startDate);
  queryParams.append('end_date', endDate);

  // apiClient 的拦截器已经返回了 response.data，所以这里直接是后端的响应体
  const result = (await apiClient.get(
    `/work-hours/calendar?${queryParams.toString()}`
  )) as ApiResponse<CalendarWorkHour[]>;

  logger.debug('getWorkHoursCalendar result:', {
    success: result.success,
    dataCount: result.data?.length,
  });

  if (!result.success) {
    const errorMsg = result.error || result.message || '查询工时日历失败';
    logger.error('getWorkHoursCalendar failed:', errorMsg);
    throw new Error(errorMsg);
  }

  logger.debug('getWorkHoursCalendar success:', { dataCount: result.data?.length });
  return result.data || [];
}
